import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Heart, Bookmark, Calendar, User, Building2, Users2,
  Upload, ScrollText, LogOut, Globe, Home, Target, Menu, X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { Overview } from "./Overview";
import { ListingManager } from "./ListingManager";
import { Leads } from "./Leads";
import { Appointments } from "./Appointments";
import { UsersManager } from "./UsersManager";
import { Favorites, SavedSearches } from "./ClientViews";
import { Profile } from "./Profile";
import { ImportAudit } from "./ImportAudit";
import { AgentsManager } from "./AgentsManager";
import { NeighborhoodsManager } from "./NeighborhoodsManager";
import { ArticlesManager } from "./ArticlesManager";
import { Logo } from "../../components/Logo";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang, changeLang } = useLang();
  const navigate = useNavigate();
  const isStaff = user.role === "admin" || user.role === "agent";
  const isAdmin = user.role === "admin";
  const [section, setSection] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);

  const clientNav = [
    { k: "overview", icon: LayoutDashboard, label: t("dash.overview") },
    { k: "favorites", icon: Heart, label: t("dash.favorites") },
    { k: "saved", icon: Bookmark, label: t("dash.savedSearches") },
    { k: "appointments", icon: Calendar, label: t("dash.appointments") },
    { k: "profile", icon: User, label: t("dash.profile") },
  ];
  const staffNav = [
    { k: "overview", icon: LayoutDashboard, label: t("dash.overview") },
    { k: "listings", icon: Building2, label: t("dash.listings") },
    { k: "leads", icon: Target, label: t("dash.leads") },
    { k: "appointments", icon: Calendar, label: t("dash.appointments") },
    ...(isAdmin ? [
      { k: "users", icon: Users2, label: t("dash.users") },
      { k: "agents", icon: Users2, label: lang === "es" ? "Agentes" : "Agents" },
      { k: "neighborhoods", icon: Home, label: lang === "es" ? "Vecindarios" : "Neighborhoods" },
      { k: "articles", icon: ScrollText, label: lang === "es" ? "Blog" : "Blog" },
      { k: "import", icon: Upload, label: t("dash.import") },
    ] : []),
    { k: "profile", icon: User, label: t("dash.profile") },
  ];
  const nav = isStaff ? staffNav : clientNav;

  const render = () => {
    switch (section) {
      case "overview": return <Overview role={user.role} />;
      case "favorites": return <Favorites />;
      case "saved": return <SavedSearches />;
      case "appointments": return <Appointments />;
      case "listings": return <ListingManager />;
      case "leads": return <Leads />;
      case "users": return <UsersManager />;
      case "import": return <ImportAudit />;
      case "agents": return <AgentsManager />;
      case "neighborhoods": return <NeighborhoodsManager />;
      case "articles": return <ArticlesManager />;
      case "profile": return <Profile />;
      default: return null;
    }
  };

  const roleLabel = {
    admin: "Administrator", agent: "Agent", client: "Client", owner: "Property Owner", referral: "Referral Partner",
  }[user.role];

  const SidebarContent = () => (
    <>
      <Link to="/" className="flex items-center gap-2.5 px-6 h-20 border-b border-border">
        <Logo className="h-9" />
      </Link>
      <div className="px-6 py-5 border-b border-border">
        <p className="font-serif text-xl truncate">{user.name}</p>
        <p className="label-caps text-primary mt-1">{roleLabel}</p>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <button key={n.k} onClick={() => { setSection(n.k); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${section === n.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
            data-testid={`dash-nav-${n.k}`}>
            <n.icon size={17} /> {n.label}
          </button>
        ))}
      </nav>
      <div className="p-3 border-t border-border space-y-1">
        <button onClick={() => changeLang(lang === "en" ? "es" : "en")} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="dash-lang">
          <Globe size={17} /> {lang === "en" ? "English" : "Español"}
        </button>
        <Link to="/" className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Home size={17} /> {t("brand")}
        </Link>
        <button onClick={async () => { await logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors" data-testid="dash-logout">
          <LogOut size={17} /> {t("nav.logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border fixed inset-y-0">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 flex flex-col bg-background border-r border-border">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <div className="lg:hidden sticky top-0 z-40 glass flex items-center justify-between px-5 h-16">
          <button onClick={() => setMobileOpen(true)} data-testid="dash-mobile-menu"><Menu /></button>
          <Logo className="h-7" />
          <span className="w-6" />
        </div>
        <div className="p-5 lg:p-10 max-w-[1300px]">{render()}</div>
      </div>
    </div>
  );
}
